import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation, withTranslation } from 'react-i18next';
import compose from 'ramda/src/compose';
import { Box, Button } from '@material-ui/core';
import LocaleKeys from '../../locales/keys';
import PaymentMethod from '../../subscriptionInfo/components/paymentMethod';
import {
  getAddNewAddressAddressList,
  getPaymentInstrumentList,
  getPaymentListModel,
  getUiModelAddresslist,
  isSequenceUpdateFormNode,
  setCardToStripeRetrievePaymentInstrumentStripeNode,
  setPaymentMethodNode,
  setRetrievePaymentInstrumentNode,
  setRetrievePaymentInstrumentStripeNode,
} from '../../subscriptionInfo/helpers/mySubscriptionHelpers';
import { getUpdatedPaymentInstrumentPayload } from '../../subscriptionInfo/helpers/paymentMethodFormSubmit';
import useUpdatePaymentInstrument from '../../common/hooks/useUpdatePaymentInstrument';
import useMetaDataCodes from '../../common/hooks/useMetaDataCodes';
import useRetrieveAddresses from '../../common/hooks/useRetrieveAddresses';
import { addNewAddressConstant } from '../../constants/addNewAddressOption.constant';
import {
  getCreditCardType,
  readSubscriberCountry,
  scrollOptions,
  scrollToTop,
} from '../../utils/common';
import useCreatePaymentInstrument from '../../common/hooks/useCreatePaymentInstrument';
import PaymentTermsAndConditions from '../components/paymentTermsAndConditions';
import ErrorScreen from '../../common/errorScreen';
import {
  getExistingSubscriptionForUpgrade,
  getIsSessionCountrySupported,
  getPaymentMethodListNodeValue,
  getRetrieveDefaultPaymentUiModelNode,
  getUpdatedAddressList,
  retrieveDefaultPaymentModel,
} from '../helpers/paymentOrderHelpers';
import { paymentFormName } from '../../constants/checkout.constant';
import { paymentMethodSaveButtonId } from '../../constants/subscription.constant';
import postMessage from '../../utils/postMessage';
import PaymentMethodStripe from '../../subscriptionInfo/components/paymentMethodStripe';
import { Elements } from '@stripe/react-stripe-js';
import { stripeStyles } from './stripeStyles';
import { useTheme } from '@material-ui/core/styles';
import { debounce, stripeFonts } from '../../utils/stripeUtility';
import {
  STRIPE_LOAD_TYPES,
  stripeElementsConfig,
} from '../../constants/stripePayment.constant';
import { subscriptionProperties } from '../../subscriptionInfo/helpers/subscriptionBasedProductCodeTables';
import { payloadTypes } from '../../constants/metaDataPayloadTypes';
import {
  isHideCheckoutStripePayment,
  stripeCompleteOrderPayloadActivity,
} from '../helpers/stripeCheckoutHelpers';
import useRemovePaymentInstrument from '../../common/hooks/useRemovePaymentInstrument';
import { setElementsOptions } from '../../subscriptionInfo/helpers/paymentMethodStripeHelpers';
import { upgradeSubscriptionEventPayload } from '../../subscriptionInfo/components/upgradeSubscription/components/helpers/upgradeSubscriptionHelper';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    backgroundColor: theme.palette.custom.white,
    borderRadius: '10px',
    border: `2px solid ${theme.palette.custom.frame}`,
    padding: '30px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '20px',
  },
  headerText: {
    textTransform: 'uppercase',
    padding: '10px 0 20px 0',
  },
  boxContainer: {
    marginBottom: '0px',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    textTransform: 'capitalize',
  },
  iconText: {
    fontFamily: 'Formula1-Wide',
  },
  iconNode: {
    justifyContent: 'space-around',
    display: 'flex',
  },
  buttonNode: {
    borderBottom: `solid 2px ${theme.palette.custom.darkRed}`,
    borderRadius: '0',
    padding: '0',
    fontSize: '15px',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'unset',
      color: theme.palette.custom.darkRed,
    },
  },
  changeButtonNode: {
    borderBottom: `solid 2px ${theme.palette.custom.darkRed}`,
    borderRadius: '0',
    padding: '0',
    textTransform: 'capitalize',
    fontSize: '13px',
    lineHeight: 1,
  },
  baseButtonNode: {
    margin: '20px 0px',
    padding: 0,
  },

  listItemNode: {
    flex: '1 1 50%',
    letterSpacing: '.2px',
    fontSize: '17px',
    [theme.breakpoints.between('xs', 'md')]: {
      margin: 0,
      width: '100%',
    },
  },
  paymentHeaderNode: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    paddingTop: '10px',
    margin: '20px 0 20px 15px',
  },
  paymentNode: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    flexDirection: 'column',
  },
  innerNode: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignContent: 'flex-start',
  },
  outerNode: {
    paddingLeft: '15px',
  },
  accountNumber: {
    paddingRight: '15px',
  },
  expirationDate: {
    paddingLeft: '15px',
  },
  wcNode: {
    paddingLeft: '15px',
    [theme.breakpoints.between('xs', 'md')]: {
      alignSelf: 'flex-start',
    },
  },
  logoContainer: {
    [theme.breakpoints.between('xs', 'md')]: {
      marginBottom: '20px',
    },
  },
  listItemContainer: {
    padding: '15px 0 0 0',
    alignItems: 'flex-start',
    [theme.breakpoints.between('xs', 'md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  freeTrialImageContainer: {
    width: '50%',
    [theme.breakpoints.between('xs', 'md')]: {
      width: '100%',
    },
  },
  affiliateImageContainer: {
    width: '150px',
  },
  button: {
    color: '#fff',
    borderRadius: '5px',
    margin: '0',
    fontWeight: 500,
    letterSpacing: 0.5,
    lineHeight: 1,
    padding: '12px 15px',
  },
  buttonContainer: {
    width: '290px',
    marginTop: '10px',
    [theme.breakpoints.down('lg')]: {
      width: '260px',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  saveButton: {
    marginTop: 0,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      boxShadow: `0 0 0 2px ${theme.palette.custom.darkRed} inset`,
      color: theme.palette.custom.lightBlack,
      background: theme.palette.custom.white,
    },
    '&:disabled': {
      backgroundColor: theme.palette.custom.frame,
    },
  },
  alert: {
    color: theme.palette.custom.darkRed,
    backgroundColor: '#f2dede',
    borderColor: '#ebccd1',
    marginBottom: '20px',
    padding: '15px',
    border: '1px solid transparent',
    borderRadius: '4px',
    fontFamily: 'Formula1-Bold',
    fontSize: '14px',
  },
  alertSpan: {
    color: theme.palette.custom.darkRed,
  },
  checkBoxButtonContainer: {
    paddingLeft: '15px',
  },
}));

function PaymentMethodContainer(props) {
  const {
    staticApiKey,
    staticApiUrl,
    stripePromise,
    shoppingCartList,
    paymentInstruments,
    defaultPaymentInstruments,
    setDefaultPayment,
    calculateOrderQuoteResponseForPayment,
    calculateOrderQuoteFaultNodeForPayment,
    isErrorAlertDisplay,
    errors,
    addressAndCodeTableFaultNode,
    completeOrder3DSHandler,
    isGenericErrorAlertDisplay,
    submitOrderUpdateCountryFault,
    submitOrderUpdateCountryFaultMessage,
    isMerchantEventHandlerFault,
    isVoucherCodeError,
    submitOrderUpdateCountryResponse,
    requestSubmitOrderUpdateCountry,
    stripePaymentList,
    defaultOrderQuote,
    defaultPaymentResponse,
    redemptionCodesPayload,
    setSubmitOrderCountryUpdateFaultMessage,
    setShowBackdrop,
    formik,
    showAdditionalSubscriberDetails,
    updateSubscriberResponse,
    setSubmitStripeOrNonStripeCardErrorStatus,
    setIsPaymentMethodFormError,
    isPaymentMethodFormError,
    isPaymentReady,
    setIsPaymentReady,
    submitOrderInteractionData,
    additionalSubscriberFormErrors,
    allSubscriptions,
    upgradePlanState,
  } = props;

  const theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles();
  const language = useSelector((state) => state.ascendon.settings.language);
  const subscriberLeadSourceCodes = useSelector(
    (state) =>
      state.ascendon.metadata?.code[payloadTypes.subscriberLeadSourceCode],
  );

  /** web api hooks */
  const [
    updatePaymentInstrumentUiModel,
    updatePaymentInstrumentFault,
    updatePaymentInstrumentLoading,
    requestUpdatePaymentInstrument,
    updatePaymentInteractionData,
  ] = useUpdatePaymentInstrument();
  const [
    externalDataCountries,
    externalDataStates,
    retrieveMetaDataFault,
    requestMetaDataCodes,
  ] = useMetaDataCodes();
  const [
    retrieveAddressesUiModel,
    retrieveAddressesFault,
    requestRetrieveAddresses,
  ] = useRetrieveAddresses();

  const [
    createPaymentInstrumentUiModel,
    createPaymentInstrumentFault,
    createPaymentInstrumentLoading,
    requestCreatePaymentInstrument,
  ] = useCreatePaymentInstrument();

  const [
    removePaymentInstrumentResponse,
    removePaymentInstrumentFault,
    requestRemovePaymentInstrument,
  ] = useRemovePaymentInstrument();

  /** use state */
  const [isMountedState, setIsMountedState] = useState(true);
  const [paymentMethodList, setPaymentMethodList] = useState([]);
  const [retrievePaymentInstrument, setRetrievePaymentInstrument] =
    useState(null);
  const [isChangeClicked, setIsChangeClicked] = useState(false);
  const [editSelectedId, setEditSelectedId] = useState(null);
  const [paymentMethodFormFault, setPaymentMethodFormFault] = useState('');
  const [
    showRemovePaymentInstrumentFault,
    setShowRemovePaymentInstrumentFault,
  ] = useState(null);
  const [formikStateValues, setFromikStateValues] = useState({});
  const [updatePaymentInstrument, setUpdatePaymentInstrument] = useState(null);
  const [isSequenceUpdateForm, setIsSequenceUpdateForm] = useState(false);
  const [addNewAddressStatus, setAddNewAddressStatus] = useState(false);
  const [addressList, setAddressList] = useState([]);
  const [sessionCountry, setSessionCountry] = useState(null);
  const [accountNumber, setAccountNumber] = useState('');
  const [isDefaultShow, setIsDefaultShow] = useState(false);
  const [
    handleChangeWithoutAccountNumber,
    setHandleChangeWithoutAccountNumber,
  ] = useState(false);
  const [selectedPaymentInstrumentId, setSelectedPaymentInstrumentId] =
    useState(0);
  const [paymentTermsAndConditions, setPaymentTermsAndConditions] = useState({
    paymentTermsAndConditions: '',
  });
  const [isCompleteOrderButtonDisabled, setIsCompleteOrderButtonDisabled] =
    useState(false);
  const [isFormError, setIsFormError] = useState(false);
  const [subscriptionProps, setSubscriptionProps] = useState({});
  const [
    isStripeRequestPaymentInstrument,
    setIsStripeRequestPaymentInstrument,
  ] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [creditCardToStripePayload, setCreditCardToStripePayload] =
    useState(null);
  const elementOptions = useRef(null);

  const getIsHideCheckoutStripePayment = isHideCheckoutStripePayment(
    defaultPaymentInstruments,
    stripePaymentList,
  );
  const hideStripePayment = getIsHideCheckoutStripePayment.value;
  const defaultOrderQuoteCurrency =
    defaultOrderQuote?.Quote?.Currency?.toLowerCase();
  const paymentMethodConfiguration =
    stripePaymentList?.StripePayment?.ConfigurationId;
  const {
    mode,
    captureMethod,
    paymentMethodCreation,
    defaultStripeCurrency,
    allowedCardBrands,
  } = stripeElementsConfig;

  const removePaymentInstrumentFaultHandler = (
    removePaymentInstrumentFault,
  ) => {
    setShowRemovePaymentInstrumentFault(removePaymentInstrumentFault);
  };

  /** Helper functions */
  /**use Effect */

  useEffect(() => {
    removePaymentInstrumentFault &&
      removePaymentInstrumentFaultHandler(removePaymentInstrumentFault);
  }, [removePaymentInstrumentFault]);

  useEffect(() => {
    if (defaultOrderQuote?.Quote?.Totals && !elementOptions.current) {
      elementOptions.current = setElementsOptions(
        defaultOrderQuote?.Quote?.Totals?.TotalAmount,
        mode,
        defaultOrderQuoteCurrency || defaultStripeCurrency,
      );
    }
  }, [defaultOrderQuote]);

  useEffect(() => {
    if (hideStripePayment && setIsPaymentReady) {
      setIsPaymentReady(true);
    }
  }, [hideStripePayment]);

  /**Initial Load */
  useEffect(() => {
    if (isMountedState && hideStripePayment) {
      postMessage('module.showLoader', {});
      requestRetrieveAddresses();
    }
    requestMetaDataCodes('countries');
    return () => {
      setIsMountedState(false);
    };
  }, [hideStripePayment]);

  useEffect(() => {
    if (updatePaymentInstrumentLoading) {
      postMessage('module.showLoader', {});
    } else if (updatePaymentInstrumentLoading === false) {
      postMessage('module.hideLoader', {});
      scrollOnHideLoader();
    }
  }, [updatePaymentInstrumentLoading]);

  useEffect(() => {
    if (paymentInstruments) {
      setPaymentMethodListUiModel(paymentInstruments);
    }
  }, [paymentInstruments]);

  useEffect(() => {
    if (createPaymentInstrumentLoading) {
      postMessage('module.showLoader', {});
    }
  }, [createPaymentInstrumentLoading]);

  useEffect(() => {
    if (
      getIsHideCheckoutStripePayment.type ===
        STRIPE_LOAD_TYPES.loadPaymentMethodStripe &&
      defaultOrderQuote
    ) {
      const selectedPaymentInstrument =
        paymentInstruments.find((item) => item?.Default) ||
        paymentInstruments[0];
      const getSubscriptionProps = subscriptionPropsActivity(
        defaultOrderQuote,
        selectedPaymentInstrument,
        stripePaymentList,
      );
      setSubscriptionProps(getSubscriptionProps);
    } else {
      setSubscriptionProps({});
    }
  }, [getIsHideCheckoutStripePayment?.type, defaultOrderQuote]);

  /** retrieveAddressesUiModel */
  useEffect(() => {
    if (retrieveAddressesUiModel) {
      const addressUiModel = retrieveAddressesUiModel?.Addresses || [];
      const getAddressList = getUiModelAddresslist(addressUiModel);
      if (isDefaultShow) {
        const BillingAddress =
          addressUiModel.find((address) => address.DefaultBilling) || {};
        setPaymentMethodList([{ BillingAddress }]);
      }
      setAddressList([...getAddressList, ...addNewAddressConstant()]);
      postMessage('module.hideLoader', {});
    }
  }, [retrieveAddressesUiModel]);

  useEffect(() => {
    if (updatePaymentInstrumentUiModel) {
      if (isStripeRequestPaymentInstrument) {
        stripeRequestPaymentInstrumentEnabledActivity(
          updatePaymentInstrumentUiModel,
        );
        return;
      }
      if (isSequenceUpdateForm) {
        const { PaymentInstrument } = updatePaymentInstrumentUiModel;
        const { Id } = PaymentInstrument;
        setDefaultPayment(Id, updatePaymentInstrumentUiModel);
        paymentInstrumentResponseHandler(updatePaymentInstrumentUiModel);
      }
      setEditSelectedId(null);
      setIsEditClicked(false);
    }
  }, [updatePaymentInstrumentUiModel]);

  useEffect(() => {
    if (createPaymentInstrumentUiModel) {
      const { PaymentInstrument } = createPaymentInstrumentUiModel;
      const { Id } = PaymentInstrument;
      setSelectedPaymentInstrumentId(Id);
      const updatePaymentInstrumentPayload = {
        Id: 0,
        PaymentInstrumentId: Id,
      };
      setUpdatePaymentInstrument({ ...updatePaymentInstrumentPayload });
      setDefaultPayment(Id, PaymentInstrument);

      paymentInstrumentResponseHandler(
        createPaymentInstrumentUiModel,
        {
          ...updatePaymentInstrumentPayload,
        },
        true,
      );
    }
  }, [createPaymentInstrumentUiModel]);

  useEffect(() => {
    if (calculateOrderQuoteResponseForPayment) {
      setPaymentMethodListUiModel(retrievePaymentInstrument, true);
    }
  }, [calculateOrderQuoteResponseForPayment]);

  useEffect(() => {
    const sessionCountry = readSubscriberCountry();

    // Verify if the session country is available in the list of countries
    const countryIsSupported = getIsSessionCountrySupported(
      externalDataCountries,
      sessionCountry,
    );
    if (countryIsSupported) {
      setSessionCountry(sessionCountry);
      requestMetaDataCodes('states');
    }
  }, [externalDataCountries]);

  useEffect(() => {
    if (calculateOrderQuoteFaultNodeForPayment) {
      setPaymentMethodListUiModel(retrievePaymentInstrument, true);
    }
  }, [calculateOrderQuoteFaultNodeForPayment]);

  useEffect(() => {
    if (createPaymentInstrumentFault) {
      setPaymentMethodFormFault(createPaymentInstrumentFault);
    }
  }, [createPaymentInstrumentFault]);

  useEffect(() => {
    if (updatePaymentInstrumentFault) {
      if (typeof updatePaymentInstrumentFault === 'string') {
        setPaymentMethodFormFault(updatePaymentInstrumentFault);
      }
    }
  }, [updatePaymentInstrumentFault]);

  useEffect(() => {
    const getFaultNode = retrieveMetaDataFault || retrieveAddressesFault;
    if (getFaultNode) {
      addressAndCodeTableFaultNode(getFaultNode);
    }
  }, [retrieveMetaDataFault, retrieveAddressesFault]);

  useEffect(() => {
    if (paymentInstruments?.length === 1) {
      isCompleteOrderButtonDisabledHandler(isEditClicked);
    } else {
      isCompleteOrderButtonDisabledHandler(isChangeClicked);
    }
  }, [
    isChangeClicked,
    paymentTermsAndConditions,
    isErrorAlertDisplay,
    isGenericErrorAlertDisplay,
    isVoucherCodeError,
    isFormError,
    paymentInstruments,
    isEditClicked,
    isPaymentMethodFormError,
  ]);

  useEffect(() => {
    // Hide loader - allow user to address issue
    if (
      isErrorAlertDisplay ||
      isGenericErrorAlertDisplay ||
      isVoucherCodeError ||
      isFormError ||
      isPaymentMethodFormError
    ) {
      postMessage('module.hideLoader', {});
      scrollOnHideLoader();
      if (isFormError) {
        setSubmitStripeOrNonStripeCardErrorStatus(true);
      }
    }
  }, [
    isErrorAlertDisplay,
    isGenericErrorAlertDisplay,
    isVoucherCodeError,
    isFormError,
    isPaymentMethodFormError,
  ]);

  useEffect(() => {
    if (removePaymentInstrumentResponse) {
      cardToStripePaymentInstrumentEnabledActivity(creditCardToStripePayload);
    }
  }, [removePaymentInstrumentResponse]);

  const submitOrderUpdateCountryAPIHandler = (
    stripeCompleteOrderPayload = {},
  ) => {
    const country = readSubscriberCountry();
    const submitOrderUpdateCountryPayload = stripeCompleteOrderPayloadActivity({
      stripeCompleteOrderPayload,
      shoppingCartList,
      redemptionCodesPayload,
      defaultPaymentResponse,
      country,
      subscriberLeadSourceCodes,
    });
    requestSubmitOrderUpdateCountry({
      submitOrderUpdateCountryPayload,
      staticApiKey,
      staticApiUrl,
      isUpgrade: upgradePlanState?.isUpgradePlan,
      ...getExistingSubscriptionForUpgrade(upgradePlanState),
    });
    postMessage('order.processingstart');
  };

  useEffect(() => {
    if (updateSubscriberResponse && hideStripePayment) {
      postMessage('module.hideLoader', {});
      submitOrderUpdateCountryAPIHandler();
    }
  }, [updateSubscriberResponse, hideStripePayment]);

  const subscriptionPropsActivity = (
    defaultOrderQuoteNode,
    paymentInstrumentsNode,
    stripePaymentListNode,
  ) => {
    const getQuoteItems = defaultOrderQuoteNode?.Quote?.Items || [];
    const subscriptionPropsList = {
      Items: getQuoteItems,
      PaymentInstrument: paymentInstrumentsNode,
    };
    const getSubscriptionProps = subscriptionProperties(
      subscriptionPropsList,
      stripePaymentListNode?.StripePayment,
    );
    return getSubscriptionProps;
  };

  const scrollOnHideLoader = () => {
    const hasAdditionalSubscriberFormErrors = additionalSubscriberFormErrors
      ? Object.keys(additionalSubscriberFormErrors)?.length > 0
      : false;
    setTimeout(() => {
      const pageHeight = document.getElementById(
        scrollOptions.id,
      )?.offsetHeight;
      const additionalSubscriberDetailsHeight = document.getElementById(
        'additionalSubscriberDetails',
      )?.offsetHeight;

      const scrollToLocation = hasAdditionalSubscriberFormErrors
        ? additionalSubscriberDetailsHeight
        : pageHeight;

      scrollToTop(scrollToLocation, 0, scrollOptions.behavior);
    }, 100);
  };

  const isCompleteOrderButtonDisabledHandler = (isButtonClicked) => {
    const isButtonDisabled =
      isButtonClicked ||
      !paymentTermsAndConditions.paymentTermsAndConditions ||
      isErrorAlertDisplay ||
      isGenericErrorAlertDisplay ||
      (isFormError && isPaymentMethodFormError);

    setIsCompleteOrderButtonDisabled(isButtonDisabled);
  };

  const paymentInstrumentResponseHandler = (
    paymentInstrumentResponseNode,
    paymentInstrumentNode = {},
    isCreatePaymentInstrument = false,
  ) => {
    const { PaymentInstrument } = paymentInstrumentResponseNode;
    const { CreditCard } = PaymentInstrument;
    const { PaymentInstrumentId } =
      updatePaymentInstrument || paymentInstrumentNode;
    const getSubscriptionNode = getPaymentListModel(
      { details: { PaymentInstrument } },
      PaymentInstrument.Id,
    );
    const [updatedPaymentInstrumentNode] = getSubscriptionNode;
    const getPaymentMethodListNode = getPaymentMethodListNodeValue(
      isCreatePaymentInstrument,
      updatedPaymentInstrumentNode,
      paymentMethodList,
      PaymentInstrumentId,
    );

    const getUpdatedAddressListValues = getUpdatedAddressList(
      addressList,
      updatedPaymentInstrumentNode,
    );
    const { paymentMethodListValues, creditCardDetails, billngAddressDetails } =
      setPaymentMethodNode(
        getPaymentMethodListNode,
        PaymentInstrumentId,
        {
          ...formikStateValues,
          ...getUpdatedAddressListValues.BillingAddress,
        },
        getUpdatedAddressListValues.addressList,
        CreditCard,
      );
    const getRetrievePaymentInstrumentNode = setRetrievePaymentInstrumentNode(
      isCreatePaymentInstrument
        ? [PaymentInstrument]
        : retrievePaymentInstrument,
      PaymentInstrumentId,
      creditCardDetails,
      billngAddressDetails,
    );
    setPaymentMethodList(paymentMethodListValues);
    setRetrievePaymentInstrument(getRetrievePaymentInstrumentNode);
    setEditSelectedId(null);
    setIsEditClicked(false);
    if (addNewAddressStatus) {
      const { addressListWithoutAddNewAddressItem, getAddressList } =
        getAddNewAddressAddressList(
          paymentInstrumentResponseNode?.PaymentInstrument?.BillingAddress,
          addressList,
        );

      setAddressList([
        ...addressListWithoutAddNewAddressItem,
        ...getAddressList,
        ...addNewAddressConstant(),
      ]);
    } else {
      return;
    }
  };

  const selectSinglePaymentInstrument = (
    defaultOrderQuoteNode,
    stripePaymentListNode,
    getRetrievePaymentInstrumentList,
  ) => {
    const [retrievePaymentInstrumentNode] = getRetrievePaymentInstrumentList;
    const getSubscriptionProps = subscriptionPropsActivity(
      defaultOrderQuoteNode,
      retrievePaymentInstrumentNode,
      stripePaymentListNode,
    );
    const { Id } = retrievePaymentInstrumentNode;
    setSubscriptionProps(getSubscriptionProps);
    setDefaultPayment(Id, null, getRetrievePaymentInstrumentList);
    setSelectedPaymentInstrumentId(Id);
  };

  const stripeRequestPaymentInstrumentEnabledActivity = (
    updatePaymentInstrumentUiModelEntity,
  ) => {
    const { PaymentInstrument } = updatePaymentInstrumentUiModelEntity;
    const { BillingAddress, StripeAccount, Id } = PaymentInstrument;
    const PaymentInstrumentId = Id;
    const getSubscriptionNode = getPaymentListModel(
      { details: { PaymentInstrument } },
      PaymentInstrument.Id,
    );
    const [updatedPaymentInstrumentNode] = getSubscriptionNode;
    const getPaymentMethodListNode = getPaymentMethodListNodeValue(
      false,
      updatedPaymentInstrumentNode,
      paymentMethodList,
      PaymentInstrumentId,
    );
    const getRetrievePaymentInstrumentNode =
      setRetrievePaymentInstrumentStripeNode(
        retrievePaymentInstrument,
        PaymentInstrumentId,
        StripeAccount,
        BillingAddress,
        PaymentInstrument,
      );
    setPaymentMethodList(getPaymentMethodListNode);
    setRetrievePaymentInstrument(getRetrievePaymentInstrumentNode);
    setEditSelectedId(null);
    setIsEditClicked(false);
    if (getRetrievePaymentInstrumentNode.length === 1) {
      selectSinglePaymentInstrument(
        defaultOrderQuote,
        stripePaymentList,
        getRetrievePaymentInstrumentNode,
      );
    }
  };

  const cardToStripePaymentInstrumentEnabledActivity = (
    updatePaymentInstrumentUiModelEntity,
  ) => {
    const { PaymentInstrument } = updatePaymentInstrumentUiModelEntity;
    const { BillingAddress, StripeAccount } = PaymentInstrument;
    const getSubscriptionNode = getPaymentListModel(
      { details: { PaymentInstrument } },
      PaymentInstrument.Id,
    );
    const [updatedPaymentInstrumentNode] = getSubscriptionNode;
    const getPaymentMethodListNode = getPaymentMethodListNodeValue(
      false,
      updatedPaymentInstrumentNode,
      paymentMethodList,
      editSelectedId,
    );
    const getRetrievePaymentInstrumentNode =
      setCardToStripeRetrievePaymentInstrumentStripeNode(
        retrievePaymentInstrument,
        editSelectedId,
        StripeAccount,
        BillingAddress,
        PaymentInstrument,
      );
    setPaymentMethodList(getPaymentMethodListNode);
    setRetrievePaymentInstrument(getRetrievePaymentInstrumentNode);
    setEditSelectedId(null);
    setIsEditClicked(false);
    if (getRetrievePaymentInstrumentNode.length === 1) {
      selectSinglePaymentInstrument(
        defaultOrderQuote,
        stripePaymentList,
        getRetrievePaymentInstrumentNode,
      );
    }
  };

  /** Event handlers */

  const handleChangeClick = () => {
    if (!retrievePaymentInstrument) return;
    const elementSort = (a, b) => b.Id - a.Id;
    const getSortedDefaultPaymentUiModel = (defaultPaymentUiModel) =>
      defaultPaymentUiModel?.sort(elementSort);
    const sortedDefaultPaymentUiModel = getSortedDefaultPaymentUiModel(
      retrievePaymentInstrument,
    );
    const getNode = [
      ...getPaymentInstrumentList(sortedDefaultPaymentUiModel, 0, false),
    ];
    setPaymentMethodList(getNode);
    setIsChangeClicked(true);
    setSubmitOrderCountryUpdateFaultMessage('');
  };

  const handleSelectClick = (selectedNode) => {
    const selectedPaymentInstrument =
      retrievePaymentInstrument.find(
        (item) => item?.Id === selectedNode?.PaymentInstrumentId,
      ) || retrievePaymentInstrument[0];
    const getSubscriptionProps = subscriptionPropsActivity(
      defaultOrderQuote,
      selectedPaymentInstrument,
      stripePaymentList,
    );
    const { PaymentInstrumentId } = selectedNode;
    setSubscriptionProps(getSubscriptionProps);
    setDefaultPayment(PaymentInstrumentId, null, retrievePaymentInstrument);
    setSelectedPaymentInstrumentId(PaymentInstrumentId);
  };

  const handleEditClick = (selectedEditNode) => {
    if (editSelectedId) {
      return;
    }
    const selectedPaymentInstrument =
      retrievePaymentInstrument.find(
        (item) => item?.Id === selectedEditNode?.PaymentInstrumentId,
      ) || retrievePaymentInstrument[0];
    if (defaultOrderQuote) {
      const getSubscriptionProps = subscriptionPropsActivity(
        defaultOrderQuote,
        selectedPaymentInstrument,
        stripePaymentList,
      );
      setSubscriptionProps(getSubscriptionProps);
    }
    setEditSelectedId(selectedEditNode?.PaymentInstrumentId);
    setIsEditClicked(true);
    setSubmitOrderCountryUpdateFaultMessage('');
  };

  const handleButtonCancel = () => {
    setPaymentMethodFormFault('');
    setEditSelectedId(null);
    setShowRemovePaymentInstrumentFault(null);
    setIsEditClicked(false);
  };

  const createWalletSaveHandler = (eventNode) => {
    setPaymentMethodFormFault('');
    const defaultPaymentInstrumentValues = {
      Default: true,
      Type: 0,
      TypeName: null,
    };
    const { formik, showAddNewAddress, postalCodeRegularExpression } =
      eventNode;
    const { values } = formik;
    setFromikStateValues({ ...values });
    const getCardType = getCreditCardType(values.CardNumber.toString());
    const getUpdatedPaymentInstruments = getUpdatedPaymentInstrumentPayload(
      values,
      {},
      showAddNewAddress,
      postalCodeRegularExpression,
      retrievePaymentInstrument,
    );
    const getPaymentInstrumentCreatePayload = {
      ...getUpdatedPaymentInstruments,
      ...defaultPaymentInstrumentValues,
      CreditCard: {
        ...getUpdatedPaymentInstruments.CreditCard,
        AccountNumber: values.CardNumber.toString(),
        Type: getCardType.Type,
        BillingAddressId: getUpdatedPaymentInstruments.BillingAddressId,
      },
    };
    requestCreatePaymentInstrument({
      PaymentInstrument: { ...getPaymentInstrumentCreatePayload },
    });
    setAddNewAddressStatus(showAddNewAddress);
  };

  const updateWalletSaveHandler = (saveNode) => {
    const { name, formik, showAddNewAddress, postalCodeRegularExpression } =
      saveNode;
    const { initialValues, values } = formik;
    const { SecurityCode, ...restUpdateValues } = values;
    setFromikStateValues({ ...values });
    setPaymentMethodFormFault('');
    setUpdatePaymentInstrument({
      Id: 0,
      PaymentInstrumentId: name,
    });
    const getSelectedPaymentInstrument = retrievePaymentInstrument.find(
      (item) => item.Id === name,
    );
    const getUpdatedPaymentInstruments = getUpdatedPaymentInstrumentPayload(
      values,
      getSelectedPaymentInstrument,
      showAddNewAddress,
      postalCodeRegularExpression,
      retrievePaymentInstrument,
    );
    const getIsSequenceFormUpdate = isSequenceUpdateFormNode(
      initialValues,
      restUpdateValues,
    );
    setIsSequenceUpdateForm(getIsSequenceFormUpdate);
    requestUpdatePaymentInstrument({
      PaymentInstrument: { ...getUpdatedPaymentInstruments },
    });
    setAddNewAddressStatus(showAddNewAddress);
  };

  const handleSave = (saveNode) => {
    if (!paymentInstruments.length) {
      createWalletSaveHandler(saveNode);
      setIsCompleteOrderButtonDisabled(true);
      return;
    }
    updateWalletSaveHandler(saveNode);
    setIsCompleteOrderButtonDisabled(true);
  };

  const handleStripeCheckoutSave = (stripePayload, isStripeToCreditCard) => {
    if (isStripeToCreditCard) {
      requestRemovePaymentInstrument({ Id: editSelectedId });
      setCreditCardToStripePayload(stripePayload);
      return;
    }

    const { updatePaymentInstrumentPayload } = stripePayload;
    setIsStripeRequestPaymentInstrument(true);
    requestUpdatePaymentInstrument({
      ...updatePaymentInstrumentPayload,
    });
  };

  const retrieveWalletEmpty = () => {
    if (retrieveAddressesUiModel?.Addresses?.length) {
      const addressUiModel = retrieveAddressesUiModel?.Addresses;
      const BillingAddress =
        addressUiModel.find((address) => address.DefaultBilling) || {};
      setPaymentMethodList([{ BillingAddress }]);
    } else {
      setPaymentMethodList([{}]);
    }
    setIsDefaultShow(true);
    setHandleChangeWithoutAccountNumber(false);
  };

  const retrieveWalletDefault = (
    retrieveDefaultPaymentUiModelNode,
    handleChangeWithoutAccountNumber,
  ) => {
    setPaymentMethodList(retrieveDefaultPaymentUiModelNode);
    setIsDefaultShow(false);
    setHandleChangeWithoutAccountNumber(handleChangeWithoutAccountNumber);
  };

  const setPaymentMethodListUiModel = (
    paymentInstruments,
    isCalculateOrderQuote = false,
  ) => {
    if (!paymentInstruments) return;
    const getRetrieveDefaultPaymentUiModel =
      retrieveDefaultPaymentModel(paymentInstruments);

    const getDefaultAccountNumber =
      getRetrieveDefaultPaymentUiModel.find((item) => item.Default)
        ?.AccountNumber || '';
    setEditSelectedId(null);
    setIsChangeClicked(false);
    setIsEditClicked(false);
    setIsSequenceUpdateForm(false);
    setAccountNumber(getDefaultAccountNumber);
    setRetrievePaymentInstrument(paymentInstruments);
    if (getRetrieveDefaultPaymentUiModel.length) {
      const { paymentUiModelNode, changeWithoutAccountNumber } =
        getRetrieveDefaultPaymentUiModelNode(
          isCalculateOrderQuote,
          getRetrieveDefaultPaymentUiModel,
          selectedPaymentInstrumentId,
        );
      retrieveWalletDefault(paymentUiModelNode, changeWithoutAccountNumber);
    } else {
      retrieveWalletEmpty();
    }
  };

  const handlePaymentTermsAndConditionChecked = (
    paymentTermsAndConditionForm,
  ) => {
    const { formik } = paymentTermsAndConditionForm;
    const { values } = formik;
    setPaymentTermsAndConditions(values);
  };

  const completeOrderSubmitHandler = () => {
    postMessage('module.showLoader', {});
    const getPaymentForSaveButtonElement = document.getElementById(
      paymentMethodSaveButtonId,
    );
    if (getPaymentForSaveButtonElement && !paymentInstruments.length) {
      postMessage('module.hideLoader', {});
      if (showAdditionalSubscriberDetails) {
        formik.handleSubmit();
      }
      getPaymentForSaveButtonElement.click();
    } else {
      completeOrder3DSHandler();
    }
  };

  const completeOrderPaymentMethodEditStripeSubmit = (
    stripeCompleteOrderPayload,
  ) => {
    setIsCompleteOrderButtonDisabled(true);
    if (showAdditionalSubscriberDetails) {
      formik.handleSubmit();
    } else {
      submitOrderUpdateCountryAPIHandler(stripeCompleteOrderPayload);
    }
  };

  const debounceCompleteOrderEditStripeSubmit = useCallback(
    debounce(completeOrderPaymentMethodEditStripeSubmit, 1000),
    [
      defaultPaymentResponse,
      redemptionCodesPayload,
      subscriberLeadSourceCodes,
      shoppingCartList,
      formik,
    ],
  );

  const debounceCompleteOrderSubmitHandler = useCallback(
    debounce(completeOrderSubmitHandler, 1000),
    [
      defaultPaymentResponse,
      redemptionCodesPayload,
      subscriberLeadSourceCodes,
      shoppingCartList,
      formik,
    ],
  );

  return (
    <Box>
      {hideStripePayment ? (
        <>
          <PaymentMethod
            classes={classes}
            paymentMethodList={paymentMethodList}
            handleChange={handleChangeClick}
            handleSelectClick={handleSelectClick}
            handleEditClick={handleEditClick}
            isChangeClicked={isChangeClicked}
            paymentInstrumentLength={retrievePaymentInstrument?.length}
            editSelectedId={editSelectedId}
            handleButtonCancel={handleButtonCancel}
            addressList={addressList}
            handleSave={handleSave}
            paymentMethodFormFault={paymentMethodFormFault}
            showRemovePaymentInstrumentFault={showRemovePaymentInstrumentFault}
            language={language}
            sessionCountry={sessionCountry}
            externalData={externalDataCountries}
            externalDataState={externalDataStates}
            isFreeTrial={false}
            subscriptionProps={subscriptionProps}
            isAccountNumber={accountNumber}
            isShowDefault={isDefaultShow}
            handleChangeWithoutAccountNumber={handleChangeWithoutAccountNumber}
            editCardDetailsLabel={
              retrievePaymentInstrument?.length < 2
                ? t(LocaleKeys.subscriptionInfo.subscriptions.change)
                : t(LocaleKeys.accountInfo.edit)
            }
            isPaymentTemplate={
              retrievePaymentInstrument?.length < 2 ? false : true
            }
            isHideSelect={true}
            setIsFormError={setIsFormError}
            stripePaymentList={stripePaymentList}
            stripePromise={stripePromise}
            isCheckout={true}
            handleStripeCheckoutSave={handleStripeCheckoutSave}
            isStripePaymentInstrumentEnabled={
              getIsHideCheckoutStripePayment.type ===
              STRIPE_LOAD_TYPES.loadPaymentMethodStripe
            }
            updatePaymentInstrumentFault={updatePaymentInstrumentFault}
            updatePaymentInstrumentUiModel={updatePaymentInstrumentUiModel}
            submitOrderUpdateCountryFault={submitOrderUpdateCountryFault}
            submitOrderUpdateCountryResponse={submitOrderUpdateCountryResponse}
            completeOrderPaymentMethodEditStripeSubmit={
              completeOrderPaymentMethodEditStripeSubmit
            }
            formik={formik}
            setIsPaymentMethodFormError={setIsPaymentMethodFormError}
            showAdditionalSubscriberDetails={showAdditionalSubscriberDetails}
            setSubmitStripeOrNonStripeCardErrorStatus={
              setSubmitStripeOrNonStripeCardErrorStatus
            }
            isPaymentReady={isPaymentReady}
            setIsPaymentReady={setIsPaymentReady}
            updatePaymentInteractionData={updatePaymentInteractionData}
            submitOrderInteractionData={submitOrderInteractionData}
            upgradePlanState={upgradePlanState}
          />
          {isErrorAlertDisplay && (
            <>
              <Box className={classes.subscriptionErrorContainer}>
                <ErrorScreen
                  error={errors[errors.length - 1]}
                  mt={5}
                  mb={5}
                  removeStyle={true}
                  variant="h2"
                  hideBackdrop={true}
                />
              </Box>
            </>
          )}
          {!isErrorAlertDisplay &&
          (submitOrderUpdateCountryFaultMessage ||
            isMerchantEventHandlerFault) ? (
            <Box className={classes.subscriptionErrorContainer}>
              <ErrorScreen
                error={submitOrderUpdateCountryFaultMessage}
                mt={5}
                mb={5}
                removeStyle={true}
                variant="h2"
                hideBackdrop={true}
              />
            </Box>
          ) : null}
          <Box className={classes.checkBoxButtonContainer}>
            <Box mb={5}>
              <PaymentTermsAndConditions
                formData={paymentTermsAndConditions}
                formName={paymentFormName}
                privacyPolicyText={t(
                  LocaleKeys.payment.orders.orderPrivacyPolicy,
                )}
                handleSave={handlePaymentTermsAndConditionChecked}
                subscriptionDisclaimText={t(
                  LocaleKeys.payment.orders.subscriptionDisclaimText,
                )}
              />
            </Box>
            <Box className={classes.buttonContainer}>
              <Button
                className={`${classes.button} ${classes.saveButton}`}
                onClick={() => {
                  if (upgradePlanState?.isUpgradePlan) {
                    const getEventPayload = upgradeSubscriptionEventPayload({
                      language,
                      selectedEligiblePlan:
                        upgradePlanState?.selectedEligiblePlan,
                    });
                    postMessage('module.orderUpgradeStart', getEventPayload);
                  }
                  if (
                    getIsHideCheckoutStripePayment.type ===
                    STRIPE_LOAD_TYPES.loadPaymentMethodStripe
                  ) {
                    const isSelectedCyberSource =
                      defaultPaymentResponse?.paymentInstrumentIds.every(
                        (item) => item?.CreditCard,
                      );
                    if (isSelectedCyberSource) {
                      debounceCompleteOrderSubmitHandler();
                    } else {
                      debounceCompleteOrderEditStripeSubmit({});
                    }
                  } else {
                    debounceCompleteOrderSubmitHandler();
                  }
                }}
                data-testid={`button-save-completeOrder`}
                disabled={isCompleteOrderButtonDisabled}
              >
                {upgradePlanState?.isUpgradePlan
                  ? t(LocaleKeys.subscriptionInfo.subscriptions.upgradeNow)
                  : t(LocaleKeys.payment.orders.completeOrder)}
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <>
          {defaultOrderQuote && elementOptions.current && (
            <Elements
              stripe={stripePromise}
              options={{
                mode: elementOptions?.current?.mode,
                currency: defaultOrderQuoteCurrency || defaultStripeCurrency,
                amount: elementOptions?.current?.amount,
                allowedCardBrands,
                captureMethod,
                paymentMethodCreation,
                payment_method_configuration: paymentMethodConfiguration,
                appearance: stripeStyles(theme),
                locale: language,
                fonts: stripeFonts,
              }}
            >
              <PaymentMethodStripe
                shoppingCartList={shoppingCartList}
                staticApiKey={staticApiKey}
                staticApiUrl={staticApiUrl}
                submitOrderUpdateCountryResponse={
                  submitOrderUpdateCountryResponse
                }
                requestSubmitOrderUpdateCountry={
                  requestSubmitOrderUpdateCountry
                }
                submitOrderUpdateCountryFault={submitOrderUpdateCountryFault}
                isCheckout={true}
                paymentMethod={
                  paymentMethodList.find((item) => item.Default) || {}
                }
                paymentInstrumentId={
                  paymentMethodList.find((item) => item.Default)
                    ?.PaymentInstrumentId || 0
                }
                setShowBackdrop={setShowBackdrop}
                redemptionCodesPayload={redemptionCodesPayload}
                formik={formik}
                showAdditionalSubscriberDetails={
                  showAdditionalSubscriberDetails
                }
                updateSubscriberResponse={updateSubscriberResponse}
                setSubmitStripeOrNonStripeCardErrorStatus={
                  setSubmitStripeOrNonStripeCardErrorStatus
                }
                setIsPaymentReady={setIsPaymentReady}
                language={language}
                externalDataStates={externalDataStates}
                updatePaymentInteractionData={updatePaymentInteractionData}
                submitOrderInteractionData={submitOrderInteractionData}
                currency={defaultOrderQuoteCurrency || defaultStripeCurrency}
                amount={elementOptions?.current?.amount}
                stripePaymentList={stripePaymentList}
                allSubscriptions={allSubscriptions}
                upgradePlanState={upgradePlanState}
              />
            </Elements>
          )}
        </>
      )}
    </Box>
  );
}

export default compose(withTranslation())(PaymentMethodContainer);
